import type { ReactNode } from 'react';
import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { FaHome, FaBell, FaSearch, FaBars, FaMapMarkerAlt } from 'react-icons/fa';
import { WiCloudy } from 'react-icons/wi';
import { useUserLocation } from '@/hooks';
import { getWeatherByCoordinates, searchLocations } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { WeatherIcon } from './WeatherIcon';
import type { Location } from '@miweather/shared';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Get user's location and weather
  const { location: userLocation } = useUserLocation();
  const { data: weatherData } = useQuery({
    queryKey: queryKeys.weather.byCoordinates(userLocation?.lat || 0, userLocation?.lon || 0),
    queryFn: () => getWeatherByCoordinates(userLocation!.lat, userLocation!.lon),
    enabled: !!userLocation,
  });

  // Search locations with debounce
  const { data: searchResults } = useQuery({
    queryKey: queryKeys.locations.search(searchQuery),
    queryFn: () => searchLocations(searchQuery),
    enabled: searchQuery.length >= 2,
    staleTime: 60 * 1000, // 1 minute
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Navigate to first result if available
    if (searchResults?.results?.length) {
      handleResultClick(searchResults.results[0]);
    }
  };

  const handleResultClick = (loc: Location) => {
    setSearchQuery('');
    setShowResults(false);
    navigate({ to: '/weather/$locationId', params: { locationId: loc.id } });
  };

  return (
    <div className="min-h-screen flex flex-col font-sans pb-16 md:pb-0">
      {/* Header */}
      <header className="py-3 px-4 glass-nav sticky top-0 z-40">
        <nav className="max-w-6xl mx-auto flex items-center gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1.5 shrink-0">
            <WiCloudy className="text-4xl text-[#f97316]" />
            <span className="text-xl font-bold tracking-tight">
              <span className="text-[#f97316]">mi</span>
              <span className="text-white">weather</span>
              <span className="text-white/50 text-sm">.org</span>
            </span>
          </Link>

          {/* Search Bar - Centered */}
          <div ref={searchRef} className="relative flex-1 max-w-xl mx-auto">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaSearch className="text-white/40 text-sm" />
                </div>
                <input
                  id="location-search"
                  type="search"
                  placeholder="Search for city or county..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowResults(true);
                  }}
                  onFocus={() => setShowResults(true)}
                  className="w-full h-10 pl-10 pr-4 bg-[#2a3a52] border-0 rounded-full text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#5cb8d6]/50"
                  aria-label="Search for Michigan city or county"
                  role="combobox"
                  aria-expanded={showResults && searchQuery.length >= 2}
                  aria-autocomplete="list"
                  aria-controls="location-search-results"
                />
              </div>
            </form>

            {/* Search Results Dropdown */}
            {showResults && searchQuery.length >= 2 && searchResults?.results && searchResults.results.length > 0 && (
              <div
                id="location-search-results"
                className="absolute top-full left-0 right-0 mt-2 bg-[#1a2a3d] rounded-xl shadow-xl border border-white/10 overflow-hidden z-50"
                role="listbox"
                aria-label="Search results"
              >
                {searchResults.results.slice(0, 6).map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => handleResultClick(loc)}
                    className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-center gap-3"
                    role="option"
                    aria-selected={false}
                  >
                    <FaMapMarkerAlt className="text-white/40 text-sm shrink-0" aria-hidden="true" />
                    <div>
                      <p className="font-medium">{loc.name}</p>
                      <p className="text-xs text-white/50 capitalize">{loc.type} • {loc.region?.replace('-', ' ')}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No results message */}
            {showResults && searchQuery.length >= 2 && searchResults?.results?.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a2a3d] rounded-xl shadow-xl border border-white/10 p-4 z-50">
                <p className="text-white/50 text-sm text-center">No locations found for "{searchQuery}"</p>
              </div>
            )}
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Current Location Weather Badge */}
            {weatherData && userLocation && (
              <Link to="/" className="flex items-center gap-2 stat-box px-3 py-1.5 rounded-full hover:bg-white/10 transition-colors">
                <WeatherIcon icon={weatherData.current.icon} size={20} className="text-white/80" />
                <span className="text-sm font-bold">{weatherData.current.temperature ?? '--'}°</span>
                <div className="hidden sm:flex items-center gap-1 text-xs text-white/60">
                  <FaMapMarkerAlt className="text-[10px]" />
                  <span>{userLocation.city}</span>
                </div>
              </Link>
            )}

            {/* Notifications */}
            <button
              className="relative p-2 text-white/70 hover:text-white transition-colors"
              aria-label="View notifications (1 new)"
            >
              <FaBell className="text-lg" aria-hidden="true" />
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center" aria-hidden="true">
                1
              </span>
            </button>

            {/* Menu */}
            <button
              className="p-2 text-white/70 hover:text-white transition-colors"
              aria-label="Open menu"
            >
              <FaBars className="text-lg" aria-hidden="true" />
            </button>
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-4">{children}</main>

      {/* Footer */}
      <footer className="hidden md:block py-4 px-4 text-center text-white/40 text-sm border-t border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-center gap-4">
          <span>© {new Date().getFullYear()} miweather.org</span>
          <span>•</span>
          <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
          <span>•</span>
          <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
          <span>•</span>
          <span>Weather data from NWS</span>
        </div>
      </footer>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 glass-nav border-t border-b-0 z-50">
        <div className="flex items-center justify-around py-2">
          <NavItem to="/" icon={<FaHome />} label="Home" active={currentPath === '/'} />
          <NavItem to="/alerts" icon={<FaBell />} label="Alerts" active={currentPath === '/alerts'} />
        </div>
      </nav>
    </div>
  );
}

function NavItem({ to, icon, label, active }: { to: string; icon: ReactNode; label: string; active: boolean }) {
  return (
    <Link
      to={to}
      className={`flex flex-col items-center gap-1 px-4 py-2 transition-colors ${
        active ? 'text-[#5cb8d6]' : 'text-white/50'
      }`}
    >
      <span className="text-lg">{icon}</span>
      <span className="text-xs">{label}</span>
    </Link>
  );
}
